import { Picture } from "@/components/media/Picture";
import { createFileRoute } from "@tanstack/react-router";

import { BookOrderForm } from "@/components/forms/BookOrderForm";
import { Footer } from "@/components/layout/Footer";
import { Header } from "@/components/layout/Header";
import { PageHero } from "@/components/layout/PageHero";
import { heroFor } from "@/data/heroes";
import { pageBlocks } from "@/lib/content";

const TITLE = "Fire Starters and Rain Crusaders — Light of Life International";
const DESCRIPTION =
  "Fire Starters and Rain Crusaders by Stephen Evans: a ten year journey of faith, miracles and crusade ministry. Order your copy.";

export const Route = createFileRoute("/fire-starters-and-rain-crusaders")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "book" },
      { property: "og:url", content: "https://loli.ardent.dev/fire-starters-and-rain-crusaders" },
    ],
    links: [{ rel: "canonical", href: "https://loli.ardent.dev/fire-starters-and-rain-crusaders" }],
  }),
  component: FireStartersPage,
});

function FireStartersPage() {
  const blocks = pageBlocks("fire-starters-and-rain-crusaders");
  const text = (i: number) => {
    const block = blocks[i];
    return block && "text" in block ? block.text : "";
  };

  const about = [text(3), text(4), text(5), text(6)].filter(Boolean);
  const endorsements = [text(11), text(12), text(13), text(14)].filter(Boolean);

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <Header overlay />

      <main id="main" className="flex-1">
        <PageHero
          title={text(0) || "Fire Starters and Rain Crusaders"}
          image={heroFor("/fire-starters-and-rain-crusaders").image}
          subtitle={text(1)}
        />

        {/* About the book + cover */}
        <section className="container-site section-y grid gap-12 md:grid-cols-[1fr_18rem]">
          <div>
            <h2 className="text-4xl text-muted-foreground sm:text-5xl">
              {text(2) || "About The Book"}
            </h2>
            <div className="mt-6 space-y-4">
              {about.map((paragraph) => (
                <p key={paragraph.slice(0, 24)} className="text-foreground">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
          <div className="flex flex-col items-center gap-8">
            <img
              src="/media/FSRC_3D_transparent-250x300-1.webp"
              alt="Fire Starters and Rain Crusaders book by Stephen Evans"
              className="w-56"
            />
            <a
              href="#grab-a-copy"
              className="rounded-md bg-accent px-10 py-4 font-heading text-lg uppercase tracking-wide text-navy transition hover:brightness-110"
            >
              Buy Now
            </a>
          </div>
        </section>

        {/* Order form */}
        <section id="grab-a-copy" className="container-site pb-24">
          <div className="rounded-3xl bg-muted px-6 py-16 sm:px-12">
            <h2 className="text-center text-4xl text-muted-foreground sm:text-5xl">
              Grab a <span className="text-brand-ink">Copy!</span>
            </h2>
            <div className="mt-12">
              <BookOrderForm bookSlug="fire-starters-and-rain-crusaders" price="$15.00" />
            </div>
          </div>
        </section>

        {/* Endorsement band */}
        <section className="relative overflow-hidden bg-navy py-24">
          <Picture
            src="/media/vm07.webp"
            alt=""
            aria-hidden
            className="absolute inset-0 h-full w-full object-cover opacity-20"
            sizes="100vw"
          />
          <div className="container-site relative">
            <h2 className="text-4xl text-white sm:text-5xl">{text(10)}</h2>
            <div className="mt-14 grid gap-10 md:grid-cols-2">
              {endorsements.map((quote) => (
                <blockquote key={quote.slice(0, 24)} className="text-white/90">
                  {quote}
                </blockquote>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
